import { createForm } from '@seek/forms-ui';
import { createValidator, isEmail, required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { Button, Stack, Text, Alert, Box } from 'braid-design-system';
import React, { useState } from 'react';

import ReCaptcha from '../../../../components/shared/ReCaptcha/ReCaptcha';
import getCaptchaToken from '../../../../components/shared/ReCaptcha/getCaptchaToken';
import { login } from '../../../../modules/Auth0/login';
import register from '../../../../modules/Auth0/register';
import { Flow, type FriendlyError } from '../../../../modules/Auth0/types';
import { recaptchaNonResetErrors } from '../../../../modules/Auth0/utils/mapError';
import { useAnalyticsContext } from '../../../../modules/analytics/AnalyticsContext';
import { Experience, type FormProps } from '../../../../types';
import getDisplayBrand from '../../../../utils/getDisplayBrand';
import getHashRoute from '../../../../utils/getHashRoute';
import { handleFormSubmit } from '../../../../utils/handleFormSubmit';
import isTestEmail from '../../../../utils/isTestEmail';
import { useAppContext } from '../../../AppContext';
import useTrackError from '../../../hooks/analytics/useTrackError';
import useTrackPageView from '../../../hooks/analytics/useTrackPageView';
import useFormLoadMetrics from '../../../hooks/metrics/useFormLoadMetrics';
import { useBotState } from '../../../hooks/useBotState';
import useFormState from '../../../hooks/useFormState';
import ConsentCheckbox from '../../../shared/ConsentCheckbox/ConsentCheckbox';
import {
  consentConfig,
  type ConsentOptionChangeTracking,
} from '../../../shared/ConsentCheckbox/utils';
import FormCard from '../../../shared/FormCard/FormCard';
import FormHeader from '../../../shared/FormHeader/FormHeader';
import FormStack from '../../../shared/FormStack';
import NewPassword from '../../../shared/NewPassword/NewPassword';
import Nudge from '../../../shared/Nudge/Nudge';
import {
  escapeSubmit,
  requireCloudflareBotCheck,
} from '../../../shared/ReCaptcha/utils';
import ServerError from '../../../shared/ServerError/ServerError';
import SocialButtons from '../../../shared/SocialButtons/SocialButtons';
import TertiaryLinks from '../../../shared/TertiaryLinks';
import TrackLink from '../../../shared/TrackLink/TrackLink';
import { isErrorPasswordRelated } from '../../PasswordResetPage/utils';
import LoginMessage from '../LoginForm/LoginMessage/LoginMessage.tsx';
import { useLoginPageContext } from '../LoginPageContext';
import { LOGIN } from '../hashRoutes';
import EmailField from '../shared/components/EmailField/EmailField';
import SingleSignOnButton from '../shared/components/SingleSignOnButton/SingleSignOnButton';

import translations from './.vocab';

import * as styles from '../LoginPageStyles.css';

interface FormValues {
  newEmailAddress: string;
  newPassword: string;
  privacyAccepted: boolean;
  marketingAccepted: boolean;
}

const { FormProvider, useField } = createForm<FormValues>();

interface Props extends FormProps<FormValues> {}

const RegistrationForm = ({ handleSubmit }: Props) => {
  const { t } = useTranslations(translations);

  const { setFlow, realm: realmOverride, loginMessage } = useLoginPageContext();
  setFlow(Flow.Register);
  const [formState, setFormState] = useFormState<FriendlyError>();
  const context = useAppContext();
  const {
    realm,
    googleCaptchaSiteKey,
    isMobileClient,
    experience,
    privacyPath,
    brand,
    getClientHref,
    tenant,
    language,
    locale,
    site,
  } = context;
  const displayBrand = getDisplayBrand(brand);
  const isBotCheckRequired = requireCloudflareBotCheck(site);
  const privacyUrl = privacyPath ?? '/privacy';
  useFormLoadMetrics('register');

  const { requirePrivacyConsent, requireMarketingConsent } =
    context.localeConfig?.[context.locale]?.[context.experience] ?? {};

  const { hasConsented } = createValidator({
    test: ({ value }: { value: boolean }) =>
      !requirePrivacyConsent || value === true,
    formatErrorMessages: (messages) => () =>
      messages[
        'To create an account, you must agree to the above terms.'
      ].format(),
    translations,
    validatorName: 'hasConsented',
  });

  const emailAddress = useField({
    id: 'newEmailAddress',
    validators: [required, isEmail],
  });
  const privacyAccepted = useField({
    id: 'privacyAccepted',
    validators: [hasConsented],
  });
  const marketingAccepted = useField({
    id: 'marketingAccepted',
  });

  const { error, loading, submitted } = formState;
  useTrackPageView('registration_form_displayed', 'registration form', {
    isPrivacyConsentOptedIn: true,
    isMarketingConsentOptedIn: true,
  });
  useTrackError(error);

  const { analytics } = useAnalyticsContext();
  const { trackLink } = analytics;
  const [botState, setBotState] = useBotState();
  const { isBotCheckDone, isRenderRecaptchaStarted, isRenderRecaptchaDone } =
    botState;
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const onSubmit = handleSubmit(async (formValues) => {
    if (!isPasswordValid) return;

    if (
      escapeSubmit({
        isBotCheckRequired,
        isBotCheckDone,
        isRenderRecaptchaStarted,
        isRenderRecaptchaDone,
      })
    )
      return;

    let captchaToken: string | undefined;

    // set captchaToken only if recaptcha is rendered and
    // stop the form submit if captchaToken is undefined
    if (
      isRenderRecaptchaDone &&
      !isTestEmail(site, formValues.newEmailAddress)
    ) {
      captchaToken = await getCaptchaToken(
        Flow.Register,
        googleCaptchaSiteKey!,
      );
      if (!captchaToken) {
        setFormState({
          error: recaptchaNonResetErrors.recaptcha_required,
        });
        return;
      }
    }

    const connection = realmOverride || realm;

    handleFormSubmit(
      register(analytics, {
        ...formValues,
        connection,
        captchaToken,
        language,
        locale,
      }).then(async (args) => {
        // stop the login if registration is successful
        // for suggested bot detection (with captchaToken)
        if (captchaToken) {
          return;
        }
        await login(analytics, {
          ...args,
          isMobileClient,
        });
      }),
      setFormState,
      'register',
    );
  });

  const handleConsentOptionChangeTracking = (
    trackingInput: ConsentOptionChangeTracking,
  ) => {
    const baseTrackingObject: Partial<ConsentOptionChangeTracking> = {};
    if (requirePrivacyConsent)
      baseTrackingObject.isPrivacyConsentOptedIn = privacyAccepted.value;
    if (requireMarketingConsent)
      baseTrackingObject.isMarketingConsentOptedIn = marketingAccepted.value;

    analytics.trackLink({
      ...baseTrackingObject,
      ...trackingInput,
    });
  };

  const showRecaptcha = !isTestEmail(site, emailAddress.value);

  const privacyLink = getClientHref(
    privacyUrl,
    experience === Experience.Hirer ? Experience.Candidate : experience,
  );

  const resetFormForPasswordErrorsOnly = () => {
    if (isErrorPasswordRelated(formState)) {
      setFormState({ loading: false, submitted: false });
    }
  };
  const isPasswordError = () => isErrorPasswordRelated(formState);

  const pageContentTranslated = {
    accountCreatedStatement: t('accountCreatedStatement'),
    continueToSignin: t('continueToSignin'),
    agreeToPrivacyStatement: t(
      'By registering you agree to SEEK’s Privacy Policy',
      {
        brand: displayBrand,
        PrivacyStatementLink: (v) => (
          <TrackLink
            key="privacy-link"
            href={privacyLink}
            trackingData={{
              eventName: 'privacy_statement_pressed',
              isThirdPartyLink: 'false',
              linkPosition: 'body',
            }}
          >
            {v}
          </TrackLink>
        ),
      },
    ),
    alreadyHaveAccount: t('Already have an account? Sign in', {
      SignInLink: (v) => (
        <TrackLink
          key="link"
          href={getHashRoute(LOGIN)}
          trackingData={{
            eventName: 'login_pressed',
            linkPosition: 'body',
            isThirdPartyLink: 'false',
          }}
        >
          {v}
        </TrackLink>
      ),
    }),
    register: t(
      experience === Experience.Hirer
        ? 'Register employer account'
        : 'Register',
    ),
  };

  const onContinueSigninClick = () => {
    trackLink({
      eventName: 'continue_sign_in_pressed',
      linkPosition: 'body',
      isThirdPartyLink: 'false',
    });

    window.location.hash = getHashRoute(LOGIN);
  };

  const isSignUpAgain = loginMessage === 'error.signup_again';

  return (
    <Stack space="none">
      {isBotCheckRequired && (
        <ReCaptcha
          formError={error}
          formLoading={loading}
          formSubmitted={submitted}
          setFormState={setFormState}
          isBotCheckDone={isBotCheckDone}
          isRenderRecaptchaStarted={isRenderRecaptchaStarted}
          isRenderRecaptchaDone={isRenderRecaptchaDone}
          setBotState={setBotState}
          flow={Flow.Register}
          showRecaptcha={showRecaptcha}
        />
      )}
      <Nudge />
      {error && (
        <Box paddingBottom="small">
          <ServerError error={error} />
        </Box>
      )}
      <FormCard>
        {!submitted && isSignUpAgain && <LoginMessage message={loginMessage} />}
        <FormHeader
          title={
            experience === Experience.Hirer
              ? 'Register as an employer'
              : 'Register'
          }
        />
        {submitted ? (
          <>
            <Alert tone="positive">
              <Text>{pageContentTranslated.accountCreatedStatement}</Text>
            </Alert>
            <Stack space="large">
              <Stack space="small">
                <Button tone="brandAccent" onClick={onContinueSigninClick}>
                  {pageContentTranslated.continueToSignin}
                </Button>
              </Stack>
            </Stack>
          </>
        ) : (
          <>
            <form id={`signup_${tenant}`} onSubmit={onSubmit} noValidate>
              <FormStack>
                <EmailField useField={useField} formName="register" />
                <Stack space="none">
                  <NewPassword
                    useField={useField}
                    forbiddenString={emailAddress.value.split('@')[0]}
                    setIsPasswordValid={setIsPasswordValid}
                    formError={isPasswordError()}
                    resetRegistrationForm={resetFormForPasswordErrorsOnly}
                  />
                  <Box paddingTop="xsmall" paddingBottom="medium">
                    <Stack space="gutter">
                      {requirePrivacyConsent ? (
                        <ConsentCheckbox
                          controller={privacyAccepted}
                          consentProps={consentConfig.privacy}
                          onCheckboxOptionChangeTracking={
                            handleConsentOptionChangeTracking
                          }
                        />
                      ) : (
                        <Text tone="secondary">
                          {pageContentTranslated.agreeToPrivacyStatement}
                        </Text>
                      )}
                      {requireMarketingConsent && (
                        <ConsentCheckbox
                          controller={marketingAccepted}
                          consentProps={consentConfig.marketing}
                          onCheckboxOptionChangeTracking={
                            handleConsentOptionChangeTracking
                          }
                        />
                      )}
                    </Stack>
                  </Box>

                  <Stack space="none">
                    <Box width="full" display="flex" justifyContent="center">
                      <div
                        id="recaptchaCheckbox"
                        className={
                          showRecaptcha
                            ? styles.recaptcha
                            : styles.hiddenRecaptcha
                        }
                      />
                    </Box>
                    <Button
                      data={{ cy: 'register' }}
                      type="submit"
                      variant="solid"
                      tone="brandAccent"
                      loading={loading}
                    >
                      {pageContentTranslated.register}
                    </Button>
                    <SingleSignOnButton />
                  </Stack>
                </Stack>
              </FormStack>
              <SocialButtons form="register" />
            </form>
            <TertiaryLinks>
              <Text>{pageContentTranslated.alreadyHaveAccount}</Text>
            </TertiaryLinks>
          </>
        )}
      </FormCard>
    </Stack>
  );
};

const RegistrationFormWithProvider = () => {
  const { emailAddress: newEmailAddress } = useLoginPageContext();
  const { language } = useAppContext();

  return (
    <FormProvider
      initialValues={{
        newEmailAddress,
        newPassword: '',
        privacyAccepted: true,
        marketingAccepted: true,
      }}
      language={language}
    >
      {({ handleSubmit }) => <RegistrationForm handleSubmit={handleSubmit} />}
    </FormProvider>
  );
};

export default RegistrationFormWithProvider;
